import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { ApiCustomDop } from '../../../api/hexa/ApiCustomDop';
import { ApiHexaBase } from '../../../api/hexa/ApiHexaBase';
import { HexaCustomDop } from '../../../api/hexa/models/CustomDopModels';
import {
  JsonObject,
  HexaInterestArea,
  ExecutionDay,
  EXECUTION_DAYS,
  DopType,
  DOP_TYPES,
} from '../../../api/hexa/models/SharedModels';
import SaveButtonComponent from '../../components/SaveButton';
import ToggleComponent from '../../components/ToggleField';
import InterestAreasSelect from '../../components/InterestAreasSelect';
import { ContentCopy } from '@mui/icons-material';
import React from 'react';
import CustomDopFileList from './CustomDopFileList';
import { JsonEditor } from 'jsoneditor-react18';
import ace from 'brace';
import 'brace/mode/json';
import 'brace/theme/dreamweaver';

const CustomDopDetail = () => {
  const isResetting = useRef(false);
  const [initialFormValue, setInitialFormValue] = useState(defaultCustomDop);
  const [isFormUnchanged, setIsFormUnchanged] = useState(true);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [customDop, setCustomDop] = useState<HexaCustomDop>(defaultCustomDop);
  const [open, setOpen] = React.useState(false);
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const { customDopId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    control,
    register,
    reset,
    getValues,
    trigger,
    setValue,
    formState: { isValid, errors },
  } = useForm<HexaCustomDop>({
    defaultValues: defaultCustomDop,
    mode: 'onChange',
  });

  const formValues = useWatch({ control });

  /**
   * Fetch custom DOP from the API and update the form with the fetched data
   */
  const fetchCustomDop = useCallback(async () => {
    if (customDopId === undefined || customDopId === 'new') return;
    try {
      setLoading(true);
      let customDop = await ApiCustomDop.show(customDopId);
      setCustomDop(customDop);
      const customDopFormValues = getCustomDopFilledForm(customDop);
      isResetting.current = true;
      reset(customDopFormValues);
      setInitialFormValue(customDopFormValues);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching custom DOP: 
        ${error.response?.data.error ?? 'Error fetching custom DOP'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customDopId, reset]);

  useEffect(() => {
    fetchCustomDop();
  }, [fetchCustomDop, trigger]);

  /**
   * Check if the form values have changed, used to enable/disable save button
   */
  useEffect(() => {
    setIsFormUnchanged(
      JSON.stringify(formValues) === JSON.stringify(initialFormValue)
    );
    if (isResetting.current) {
      trigger();
      isResetting.current = false;
    }
  }, [formValues, initialFormValue, trigger]);

  /**
   * Submit the custom DOP form data to the API and navigate to the new DOP detail page
   */
  const onSubmit = useCallback(async () => {
    if (customDopId === undefined) return;
    setLoading(true);
    const data: HexaCustomDop = getValues();
    try {
      const res = await ApiCustomDop.createOrUpdate(customDopId, data);
      if (res.id) {
        navigate(`../${res.id}`);
      }
      setInitialFormValue(data);
      reset(data);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} updating or creating the custom DOP: 
        ${error.response?.data.error ?? 'Error updating or creating the custom DOP'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customDopId, getValues, navigate, reset]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
    setError({
      open: false,
      message: error.message,
    });
  };

  const textField = (label: keyof HexaCustomDop, isRequired = false) => {
    return (
      <>
        <Grid2 xs={1}>
          <Typography variant="body1" component="div">
            {t(`custom_dop_list.${label}`)}
          </Typography>
        </Grid2>
        <Grid2 xs={1}>
          <TextField
            size="small"
            fullWidth
            placeholder={t('text_field.placeholder')}
            error={!!errors[label]}
            helperText={<>{errors[label]?.message}</>}
            {...register(label, {
              required: t('text_field.required'),
            })}
          />
        </Grid2>
      </>
    );
  };

  const onJsonUpdate = (newData: JsonObject, field: keyof HexaCustomDop) => {
    let data = getValues();
    data.entity_type = newData;
    setValue(field, newData);
    return true;
  };

  const handleCopyToClipboard = useCallback(async () => {
    navigator.clipboard.writeText(getValues().file_name);
    setOpen(true);
  }, [getValues]);

  return (
    <>
      <Container>
        <Card
          component="form"
          sx={{
            borderRadius: '0.5rem !important',
            paddingX: '1rem',
          }}
        >
          <CardHeader
            title={
              <Typography variant="h3">{t('custom_dop_detail')}</Typography>
            }
          />
          <Stack
            direction="row"
            pb={'0.5rem'}
            pr={'1.5rem'}
            justifyContent="flex-end"
          >
            <SaveButtonComponent
              isLoading={isLoading}
              isFormUnchanged={isFormUnchanged && customDopId !== 'new'}
              isValid={isValid}
              onSubmit={onSubmit}
            ></SaveButtonComponent>
          </Stack>
          <CardContent>
            <Stack direction="column" spacing={1}>
              <Grid2
                container
                spacing={2}
                columns={{ xs: 1, md: 2 }}
                alignItems="flex-start"
              >
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.id`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Automatically generated"
                    InputProps={{
                      readOnly: true,
                    }}
                    {...register('id')}
                  />
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.file_name`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <TextField
                    size="small"
                    fullWidth
                    error={!!errors.file_name}
                    helperText={errors.file_name?.message}
                    placeholder="Automatically generated"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            disabled={customDopId === 'new'}
                            aria-label="copy key to clipboard"
                            color="primary"
                            onClick={() => handleCopyToClipboard()}
                          >
                            <ContentCopy sx={{ fontSize: '1.3rem' }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register('file_name')}
                  />
                </Grid2>
                {textField('description', true)}
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.json_schema`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  {customDop.json_schema && (
                    <JsonEditor
                      ace={ace}
                      theme="ace/theme/dreamweaver"
                      schema={customDop.json_schema}
                      value={customDop.json_schema}
                      navigationBar={false}
                      statusBar={false}
                      allowedModes={['code', 'tree']}
                      onChange={(newData) =>
                        onJsonUpdate(newData, 'json_schema')
                      }
                    />
                  )}
                </Grid2>
                <ToggleComponent<HexaCustomDop>
                  label="custom_dop_list.delta"
                  controlGroup={control}
                  controlName="delta"
                />
                <ToggleComponent<HexaCustomDop>
                  label="custom_dop_list.enabled"
                  controlGroup={control}
                  controlName="enabled"
                />
                <Controller
                  control={control}
                  name="interest_areas"
                  render={({ field: { onChange, value } }) => (
                    <InterestAreasSelect
                      value={value ?? []}
                      onChange={onChange}
                    />
                  )}
                />
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.sub_tags`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  {customDop.sub_tags && (
                    <JsonEditor
                      ace={ace}
                      theme="ace/theme/dreamweaver"
                      schema={customDop.sub_tags}
                      value={customDop.sub_tags}
                      navigationBar={false}
                      statusBar={false}
                      allowedModes={['code', 'tree']}
                      onChange={(newData) => onJsonUpdate(newData, 'sub_tags')}
                    />
                  )}
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.dop_type`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <Controller
                    control={control}
                    name="dop_type"
                    rules={{
                      required: 'text_field.required',
                    }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <>
                        <Select
                          fullWidth
                          displayEmpty
                          size="small"
                          value={value as DopType}
                          onChange={onChange}
                          error={!!error?.message}
                          renderValue={(value: DopType) => {
                            if (!value) {
                              return (
                                <Typography color="gray">
                                  {t('custom_dop_list.dop_type_placeholder')}
                                </Typography>
                              );
                            }
                            return value;
                          }}
                        >
                          <MenuItem key="clear" value="">
                            Clear selection
                          </MenuItem>
                          {DOP_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography
                            sx={{ fontSize: '0.75rem', marginLeft: '0.75rem' }}
                            color="error"
                          >
                            {t(`${error.message}`)}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.entity_type`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  {customDop.entity_type && (
                    <JsonEditor
                      ace={ace}
                      theme="ace/theme/dreamweaver"
                      schema={customDop.entity_type}
                      value={customDop.entity_type}
                      navigationBar={false}
                      statusBar={false}
                      allowedModes={['code', 'tree']}
                      onChange={(newData) =>
                        onJsonUpdate(newData, 'entity_type')
                      }
                    />
                  )}
                </Grid2>
                <Grid2 xs={1}>
                  <Typography variant="body1" component="div">
                    {t(`custom_dop_list.execution_days`)}
                  </Typography>
                </Grid2>
                <Grid2 xs={1}>
                  <Controller
                    control={control}
                    name="execution_days"
                    render={({ field: { onChange, value } }) => (
                      <Select
                        fullWidth
                        displayEmpty
                        size="small"
                        value={value as ExecutionDay[]}
                        onChange={onChange}
                        multiple
                        renderValue={(value: ExecutionDay[]) => {
                          if (value && value.length === 0) {
                            return (
                              <Typography color="gray">
                                {t(
                                  'custom_dop_list.execution_days_placeholder'
                                )}
                              </Typography>
                            );
                          } else if (value) return value.join(', ');
                        }}
                      >
                        {EXECUTION_DAYS.map((day) => (
                          <MenuItem key={day} value={day}>
                            {day}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </Grid2>
              </Grid2>
              {customDop.file_name !== '' && (
                <CustomDopFileList
                  customDopId={customDop.id.toString()}
                  fileName={customDop.file_name}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
        <Outlet />
      </Container>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
      <Snackbar
        sx={{
          '& .MuiPaper-root': {
            padding: '1rem !important',
          },
        }}
        open={open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="info">
          {t('copied_message')}
        </Alert>
      </Snackbar>
    </>
  );
};

const defaultCustomDop: HexaCustomDop = {
  id: '',
  file_name: '',
  description: '',
  json_schema: null,
  delta: false,
  interest_areas: new Array<HexaInterestArea>(),
  sub_tags: null,
  dop_type: '',
  entity_type: null,
  enabled: false,
  execution_days: [],
};

function getCustomDopFilledForm(customDop: HexaCustomDop): HexaCustomDop {
  return {
    id: customDop.id,
    file_name: customDop.file_name,
    description: customDop.description,
    json_schema: customDop.json_schema,
    delta: customDop.delta,
    interest_areas: customDop.interest_areas,
    sub_tags: customDop.sub_tags,
    dop_type: customDop.dop_type,
    entity_type: customDop.entity_type,
    enabled: customDop.enabled,
    execution_days: customDop.execution_days,
  };
}

export default CustomDopDetail;
